import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';

import { type Entity, identityEntityGetEntities, identityEntityGetEntity } from '@column/api';
import { Icon } from '@column/column-ui-kit';
import { Popover, PopoverContent, PopoverTrigger, cn } from '@column/ui';

import { useEntityApiMode } from '../_util/entityApiMode';
import { useDebounce } from '~/hooks/useDebounce';
import {
  FADE_ONLY_MOTION,
  FadeSwap,
  OVERLAP_CELL,
  OVERLAP_GRID,
  POPOVER_CONTENT_CLASS,
  type SelectorOption,
  SelectorSearch,
  SelectorTrigger,
  TRIGGER_HEIGHT_PX,
  highlightMatch,
} from '~/lib/components/selectors';
import { useSessionStore } from '~/stores/Session';

// List responses only carry the display name on the top-level `name`; person_details is get-only.
const personName = (entity?: Entity): string =>
  entity?.name?.trim() ||
  [entity?.person_details?.first_name, entity?.person_details?.last_name].filter(Boolean).join(' ').trim();

// Collapses to zero height when nothing is selected so it can share a cell with the dropdown
// trigger and morph between the two — mirrors SelectedCard.
const SelectedPersonCard: FC<{
  open: boolean;
  name: string;
  entityId: string;
  isLoading?: boolean;
  hasError?: boolean;
  className?: string;
  onClear: () => void;
}> = ({ open, name, entityId, isLoading, hasError, className, onClear }) => {
  // Keep rendering the last content while collapsing so it fades out with the card.
  const snapshot = useRef({ name, entityId, isLoading });
  if (open) {
    snapshot.current = { name, entityId, isLoading };
  }
  const view = open ? { name, entityId, isLoading } : snapshot.current;

  return (
    <div
      aria-hidden={!open}
      className={cn(
        'grid grid-cols-1 overflow-hidden rounded-lg bg-card text-card-foreground shadow-surface-raised',
        'dark:bg-gray-800 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)]',
        'transition-all duration-200 ease-out motion-reduce:transition-none',
        open ? 'grid-rows-[1fr] opacity-100' : 'pointer-events-none grid-rows-[0fr] opacity-0',
        hasError && 'ring-1 ring-destructive',
        className
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-400 shadow-[inset_0_0_0_1px_rgb(0_0_0/0.05)] dark:bg-white/5 dark:shadow-[inset_0_0_0_1px_rgb(255_255_255/0.08)]">
            <Icon.User />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-foreground">
              {view.isLoading ? 'Loading…' : view.name || 'Person'}
            </div>
            {!view.isLoading && <div className="truncate font-mono text-xs text-muted-foreground">{view.entityId}</div>}
          </div>
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear selection"
            className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground dark:hover:bg-white/10"
          >
            {/* The ui-kit Icon scales via the --icon-size CSS var (its viewBox stays 20, so the
                drawing scales and stays centered). It sets that var on the svg itself, so a
                className can't win the cascade — set it inline to force 16px. */}
            <Icon.Close style={{ '--icon-size': '16px' } as React.CSSProperties} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Server-driven PERSON entity search + selected-entity enrichment, modeled on
// useBankAccountOptions: an infinite list keyed by the (debounced) search text, plus a
// get-by-id query so the selected person's card resolves even when it isn't on the page.
const usePersonEntityOptions = (value?: string) => {
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);
  // On the root entity page an explicit mode is provided (its Live/Sandbox tab is independent of the
  // global session mode); elsewhere this is undefined and we fall back to the global session client.
  const apiMode = useEntityApiMode();
  const requestOptions = apiMode?.requestOptions;
  const scope = apiMode?.scope ?? [platformId, isSandbox];
  const [searchText, setSearchText] = useState<string | undefined>();

  const entitiesQuery = useInfiniteQuery({
    queryKey: ['personEntitySelector', 'list', ...scope, searchText],
    queryFn: async ({ pageParam, signal }) => {
      const startingAfter = typeof pageParam === 'string' ? pageParam : undefined;
      const { data } = await identityEntityGetEntities({
        query: { type: 'PERSON', query: searchText || undefined, starting_after: startingAfter, limit: 20 },
        signal,
        throwOnError: true,
        ...(requestOptions ?? {}),
      });
      return data;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.has_more) return undefined;
      return lastPage.entities?.[lastPage.entities.length - 1]?.id;
    },
    initialPageParam: undefined as string | undefined,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const people = useMemo(() => {
    const entries = entitiesQuery.data?.pages.flatMap((page) => page?.entities ?? []) ?? [];
    return [...new Map(entries.map((entry) => [entry.id, entry])).values()];
  }, [entitiesQuery.data]);

  const activeEntityQuery = useQuery({
    queryKey: ['personEntitySelector', 'get', ...scope, value || undefined],
    queryFn: async () => {
      const { data } = await identityEntityGetEntity({
        path: { entity_id: value as string },
        throwOnError: true,
        ...(requestOptions ?? {}),
      });
      return data;
    },
    enabled: Boolean(value),
    placeholderData: () => people.find((person) => person.id === value),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const [search] = useDebounce((query?: string) => setSearchText(query || undefined), 300, []);

  const { fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData } = entitiesQuery;
  const handleScrollEnd = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isPlaceholderData) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData]);

  return {
    people,
    activePerson: activeEntityQuery.data,
    activeIsLoading: activeEntityQuery.isLoading,
    search,
    handleScrollEnd,
  };
};

export interface PersonEntitySelectorProps {
  value: string;
  onChange: (id: string) => void;
  onBlur?: () => void;
  hasError?: boolean;
  // Name to show when the linked person isn't in the fetched data (e.g. on edit before/without a lookup).
  fallbackName?: string;
}

/**
 * Links an existing PERSON entity: a placeholder trigger that morphs into a
 * server-searched dropdown, collapsing into a detail card once a person is chosen.
 * Mirrors the ACH transfer bank-account picker and reuses its primitives.
 */
export const PersonEntitySelector: FC<PersonEntitySelectorProps> = ({
  value,
  onChange,
  onBlur,
  hasError,
  fallbackName,
}) => {
  const [open, setOpen] = useState(false);
  // Mirrors the search input so option rows can highlight the matched substring.
  const [query, setQuery] = useState('');
  // Set when a selection (not an escape/click-outside) closes the popover, so we can skip
  // Radix's focus-restore onto the now-hidden trigger — see PopoverContent onCloseAutoFocus.
  const selectedOnClose = useRef(false);
  const { people, activePerson, activeIsLoading, search, handleScrollEnd } = usePersonEntityOptions(value);

  const person = activePerson ?? people.find((entity) => entity.id === value);
  const resolvedName = personName(person) || fallbackName?.trim() || '';

  const options: SelectorOption[] = useMemo(
    () =>
      people.map((entity) => ({
        id: entity.id,
        primary: highlightMatch(personName(entity) || 'Unnamed', query),
        secondary: (
          <span className="font-mono">
            <span className="text-muted-foreground/60">ID </span>
            {highlightMatch(entity.id, query)}
          </span>
        ),
      })),
    [people, query]
  );

  return (
    <div className={OVERLAP_GRID}>
      <SelectedPersonCard
        className={OVERLAP_CELL}
        open={Boolean(value)}
        name={resolvedName}
        entityId={person?.id ?? value}
        isLoading={!resolvedName && !person && activeIsLoading}
        hasError={hasError}
        onClear={() => onChange('')}
      />
      <FadeSwap visible={!value} className={OVERLAP_CELL}>
        <Popover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) {
              onBlur?.();
              setQuery('');
              search('');
            }
          }}
        >
          <PopoverTrigger asChild>
            <SelectorTrigger label="Select a person" hasError={hasError} />
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={-TRIGGER_HEIGHT_PX}
            className={POPOVER_CONTENT_CLASS}
            {...FADE_ONLY_MOTION}
            onCloseAutoFocus={(event) => {
              if (selectedOnClose.current) {
                selectedOnClose.current = false;
                event.preventDefault();
              }
            }}
          >
            <SelectorSearch
              options={options}
              searchPlaceholder="Search people…"
              emptyMessage="No people found"
              onSearch={(next) => {
                setQuery(next);
                search(next);
              }}
              onSelect={(id) => {
                selectedOnClose.current = true;
                onChange(id);
                setOpen(false);
                setQuery('');
                search('');
              }}
              onScrollEnd={handleScrollEnd}
            />
          </PopoverContent>
        </Popover>
      </FadeSwap>
    </div>
  );
};
